import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Typography,
  Button,
  Card,
  CardContent,
  useTheme,
  Chip,
  IconButton
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { formatCurrency } from 'src/common/utils/currency';
import { formatDateTime } from 'src/common/utils/date';
import { authenticationService } from 'src/services';
import { get } from 'src/services/httpClient.service';
import { getResponsiveStyles, isMobileScreen } from 'src/common/utils/responsive';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';

const PaymentHistoryList = () => {
  const theme = useTheme();
  const styles = getResponsiveStyles(theme);
  const [isMobile, setIsMobile] = useState(isMobileScreen(theme));
  const APIURL = process.env.REACT_APP_BASE_API_URL;
  const navigate = useNavigate();
  const { vendorId, franchiseId } = authenticationService.getVendorFranchiseIds();

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [loading, setLoading] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(isMobileScreen(theme));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [theme]);

  // Fetch data
  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await get(
        `${APIURL}/api/payment-transfer-history/vendor/${vendorId}/franchise/${franchiseId}/findAll`
      );
      setRows(res?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [vendorId, franchiseId]);

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleAddPayments = () => {
    navigate('/paymentTransfer/PaymentTransfer');
  };

  const handleRefresh = () => {
    fetchData();
  };

  // Mobile card render
  const renderHistoryCard = (row, index) => {
    const isExpanded = expandedCards[index];

    return (
      <Card key={index} sx={styles.mobileCard}>
        <Box sx={styles.mobileCardHeader}>
          <Box>
            <Typography sx={styles.mobileCardTitle}>
              {row.operatorName}
            </Typography>
            <Typography sx={styles.mobileCardSubText}>
              Transferred By: {row.transferredName}
            </Typography>
          </Box>
          <Chip
            label={`₹${formatCurrency(row.transferedAmount)}`}
            size="small"
            sx={{
              backgroundColor: '#2e7d32',
              color: 'white',
              fontWeight: 'bold'
            }}
          />
        </Box>

        <Typography sx={{ ...styles.mobileCardText, mt: 1 }}>
          💰 Amount: {formatCurrency(row.transferedAmount)}
        </Typography>

        <Typography sx={{ ...styles.mobileCardText, mt: 0.5 }}>
          📊 Before Balance: {formatCurrency(row.beforeBalance)}
        </Typography>

        <Typography sx={{ ...styles.mobileCardText, mt: 0.5 }}>
          📈 After Balance: {formatCurrency(row.afterBalance)}
        </Typography>

        <Typography sx={{ ...styles.mobileCardText, mt: 0.5 }}>
          🏦 Distributor Balance: {formatCurrency(row.distributorBalance)}
        </Typography>

        <Typography sx={{ ...styles.mobileCardText, mt: 0.5 }}>
          📅 Date: {formatDateTime(row.createdDate)}
        </Typography>

        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            onClick={() =>
              setExpandedCards((prev) => ({
                ...prev,
                [index]: !prev[index]
              }))
            }
          >
            {isExpanded ? 'Hide Details' : 'View More'}
          </Button>
        </Box>

        {isExpanded && (
          <Box sx={{ mt: 1.5, fontSize: '13px', opacity: 0.9 }}>
            {row.transferredBy && (
              <Typography>Transferred By ID: {row.transferredBy}</Typography>
            )}
            {row.operatorId && (
              <Typography>Operator ID: {row.operatorId}</Typography>
            )}
            {row.id && (
              <Typography>Record ID: {row.id}</Typography>
            )}
          </Box>
        )}
      </Card>
    );
  };

  const paginatedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header Section */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center",
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 2 : 0
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton onClick={() => navigate(-1)} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 600 }}>
              Payment Transfer History
            </Typography>
          </Box>
          
          <Box sx={{ display: "flex", gap: 2, width: isMobile ? "100%" : "auto" }}>
            <Button
              variant="outlined"
              onClick={handleRefresh}
              startIcon={<RefreshIcon />}
              disabled={loading}
              fullWidth={isMobile}
              size={isMobile ? "small" : "medium"}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddPayments}
              fullWidth={isMobile}
              size={isMobile ? "small" : "medium"}
            >
              Distribute
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* History List Section */}
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography 
          variant="subtitle2" 
          color="text.secondary" 
          sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}
        >
          Total Records: {rows.length}
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <Typography>Loading...</Typography>
          </Box>
        ) : rows.length === 0 ? (
          <Box display="flex" justifyContent="center" p={3}>
            <Typography color="text.secondary">No payment history found</Typography>
          </Box>
        ) : (
          <>
            {isMobile ? (
              // 📱 MOBILE VIEW - Card Layout
              <Box sx={styles.mobileCardContainer}>
                {paginatedRows.map((row, index) => renderHistoryCard(row, index))}

                {/* Pagination for mobile */}
                {rows.length > rowsPerPage && (
                  <Box sx={styles.mobileLoadMore}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        if (page + 1 < Math.ceil(rows.length / rowsPerPage)) {
                          setPage(page + 1);
                        }
                      }}
                      disabled={page + 1 >= Math.ceil(rows.length / rowsPerPage)}
                      fullWidth
                    >
                      Load More ({paginatedRows.length}/{rows.length})
                    </Button>
                  </Box>
                )}
              </Box>
            ) : (
              // 💻 DESKTOP VIEW - Table Layout
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><b>LCO</b></TableCell>
                        <TableCell><b>Transferred By</b></TableCell>
                        <TableCell><b>Amount</b></TableCell>
                        <TableCell><b>Before Balance</b></TableCell>
                        <TableCell><b>After Balance</b></TableCell>
                        <TableCell><b>Distributor Balance</b></TableCell>
                        <TableCell><b>Transferred Date</b></TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {paginatedRows.map((row, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{row.operatorName}</TableCell>
                          <TableCell>{row.transferredName}</TableCell>
                          <TableCell>
                            <Chip
                              label={formatCurrency(row.transferedAmount)}
                              size="small"
                              sx={{
                                backgroundColor: '#2e7d32',
                                color: 'white',
                                fontWeight: 'bold'
                              }}
                            />
                          </TableCell>
                          <TableCell>{formatCurrency(row.beforeBalance)}</TableCell>
                          <TableCell>{formatCurrency(row.afterBalance)}</TableCell>
                          <TableCell>{formatCurrency(row.distributorBalance)}</TableCell>
                          <TableCell>{formatDateTime(row.createdDate)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  component="div"
                  count={rows.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default PaymentHistoryList;